// ============================================================
// FIXED: SalesByProductReportPage.tsx
// ============================================================
// src/features/reports/pages/SalesByProductReportPage.tsx

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { Card, CardContent } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Badge } from '@/components/ui/Badge';
import { getBranches } from '@/features/settings/services/settingsApi';
import { getSalesByProduct, getExportUrl } from '../services/reportsApi';
import { ReportHeader } from '../components/ReportHeader';
import { ReportFilters } from '../components/ReportFilters';
import { SummaryCard } from '../components/SummaryCard';
import {
    Package, DollarSign, TrendingUp, Percent
} from 'lucide-react';

export function SalesByProductReportPage() {
    const user = useAuthStore((s) => s.user);
    const tenantId = user?.tenantId || '';

    const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
    const [branchId, setBranchId] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [sortBy, setSortBy] = useState<'revenue' | 'quantity' | 'profit'>('revenue');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

    const { data: branches } = useQuery({
        queryKey: ['branches-report', tenantId],
        queryFn: () => getBranches(tenantId),
        enabled: !!tenantId,
    });

    const params = {
        startDate: dateRange.startDate || undefined,
        endDate: dateRange.endDate || undefined,
        branchId: branchId || undefined,
    };

    const { data, isLoading } = useQuery({
        queryKey: ['report-sales-by-product', tenantId, params],
        queryFn: () => getSalesByProduct(tenantId, params),
        enabled: !!tenantId,
    });

    const formatNaira = (val: string | number) =>
        `₦${parseFloat(String(val || '0')).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

    const formatNumber = (val: string | number) =>
        parseFloat(String(val || '0')).toLocaleString();

    const branchesList = (branches || []) as any[];

    const setQuickDate = (days: number) => {
        const end = new Date();
        const start = new Date();
        if (days === 0) {
            start.setHours(0, 0, 0, 0);
        } else {
            start.setDate(start.getDate() - days);
        }
        setDateRange({
            startDate: start.toISOString().split('T')[0],
            endDate: end.toISOString().split('T')[0],
        });
    };

    const handleExport = (format: 'pdf' | 'excel') => {
        const url = getExportUrl(tenantId, 'sales_by_product', format, params as any);
        window.open(url, '_blank');
    };

    // FIXED: Convert to proper boolean
    const hasFilters = !!(dateRange.startDate || dateRange.endDate || branchId);

    const clearFilters = () => {
        setDateRange({ startDate: '', endDate: '' });
        setBranchId('');
    };

    const products = data?.products || [];
    const sortedProducts = [...products].sort((a: any, b: any) => {
        const multiplier = sortOrder === 'desc' ? -1 : 1;
        if (sortBy === 'revenue') return (parseFloat(b.revenue) - parseFloat(a.revenue)) * multiplier;
        if (sortBy === 'quantity') return (b.quantity - a.quantity) * multiplier;
        if (sortBy === 'profit') return (parseFloat(b.profit) - parseFloat(a.profit)) * multiplier;
        return 0;
    });

    const toggleSort = (field: 'revenue' | 'quantity' | 'profit') => {
        if (sortBy === field) {
            setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
        } else {
            setSortBy(field);
            setSortOrder('desc');
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
            <ReportHeader
                title="Sales by Product"
                description="Product performance analytics"
                icon={Package}
                iconGradient="from-blue-500 to-indigo-600"
                hasFilters={hasFilters}
                showFilters={showFilters}
                onToggleFilters={() => setShowFilters(!showFilters)}
                onExportPdf={() => handleExport('pdf')}
                onExportExcel={() => handleExport('excel')}
                branchName={branchesList.find(b => b.id === branchId)?.name}
            />

            <div className="max-w-[1600px] mx-auto p-6 lg:p-8 space-y-5">
                <ReportFilters
                    visible={showFilters}
                    dateRange={dateRange}
                    branchId={branchId}
                    branches={branchesList}
                    onDateRangeChange={setDateRange}
                    onBranchChange={setBranchId}
                    onClear={clearFilters}
                    onQuickDate={setQuickDate}
                />

                {isLoading ? (
                    <div className="flex justify-center py-20"><Spinner size="xl" /></div>
                ) : !data || products.length === 0 ? (
                    <EmptyState
                        icon={<Package className="w-10 h-10" />}
                        title="No Product Sales Data"
                        description="No sales data found for the selected period. Try adjusting your filters."
                        size="lg"
                    />
                ) : (
                    <>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <SummaryCard
                                label="Total Products Sold"
                                value={formatNumber(data.summary?.totalProducts || 0)}
                                icon={Package}
                                gradient="from-blue-500 to-indigo-600"
                            />
                            <SummaryCard
                                label="Total Revenue"
                                value={formatNaira(data.summary?.totalRevenue || 0)}
                                icon={DollarSign}
                                gradient="from-emerald-500 to-teal-600"
                            />
                            <SummaryCard
                                label="Total Profit"
                                value={formatNaira(data.summary?.totalProfit || 0)}
                                icon={TrendingUp}
                                gradient="from-purple-500 to-violet-600"
                            />
                            <SummaryCard
                                label="Avg. Margin"
                                value={`${(parseFloat(data.summary?.totalProfit || '0') / parseFloat(data.summary?.totalRevenue || '1') * 100).toFixed(1)}%`}
                                icon={Percent}
                                gradient="from-amber-500 to-orange-600"
                            />
                        </div>

                        <Card className="overflow-hidden">
                            <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
                                <h3 className="text-sm font-semibold text-slate-800">Product Performance</h3>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => toggleSort('revenue')}
                                        className={`text-xs px-3 py-1 rounded-lg border ${sortBy === 'revenue' ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'border-slate-200 text-slate-600'}`}
                                    >
                                        Revenue {sortBy === 'revenue' && (sortOrder === 'desc' ? '↓' : '↑')}
                                    </button>
                                    <button
                                        onClick={() => toggleSort('quantity')}
                                        className={`text-xs px-3 py-1 rounded-lg border ${sortBy === 'quantity' ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'border-slate-200 text-slate-600'}`}
                                    >
                                        Quantity {sortBy === 'quantity' && (sortOrder === 'desc' ? '↓' : '↑')}
                                    </button>
                                    <button
                                        onClick={() => toggleSort('profit')}
                                        className={`text-xs px-3 py-1 rounded-lg border ${sortBy === 'profit' ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'border-slate-200 text-slate-600'}`}
                                    >
                                        Profit {sortBy === 'profit' && (sortOrder === 'desc' ? '↓' : '↑')}
                                    </button>
                                </div>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-slate-100">
                                    <thead>
                                        <tr className="bg-slate-50/50">
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Product</th>
                                            <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Quantity Sold</th>
                                            <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Revenue</th>
                                            <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Cost</th>
                                            <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Profit</th>
                                            <th className="px-6 py-4 text-center text-xs font-semibold text-slate-500 uppercase">Margin</th>
                                            <th className="px-6 py-4 text-center text-xs font-semibold text-slate-500 uppercase">Transactions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50">
                                        {sortedProducts.map((product: any, i: number) => (
                                            <tr key={i} className="hover:bg-slate-50 transition-colors">
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                                                            <Package className="w-4 h-4 text-blue-600" />
                                                        </div>
                                                        <span className="text-sm font-medium text-slate-900">{product.name}</span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-right text-sm font-semibold">
                                                    {formatNumber(product.quantity)}
                                                </td>
                                                <td className="px-6 py-4 text-right text-sm font-semibold text-slate-900">
                                                    {formatNaira(product.revenue)}
                                                </td>
                                                <td className="px-6 py-4 text-right text-sm text-slate-500">
                                                    {formatNaira(product.cost)}
                                                </td>
                                                <td className="px-6 py-4 text-right text-sm font-bold">
                                                    <span className={parseFloat(product.profit) >= 0 ? 'text-emerald-600' : 'text-rose-600'}>
                                                        {formatNaira(product.profit)}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    <Badge
                                                        variant={parseFloat(product.margin) > 30 ? 'success' : parseFloat(product.margin) > 10 ? 'warning' : 'danger'}
                                                        size="sm"
                                                    >
                                                        {product.margin}%
                                                    </Badge>
                                                </td>
                                                <td className="px-6 py-4 text-center text-sm text-slate-500">
                                                    {product.count}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </Card>
                    </>
                )}
            </div>
        </div>
    );
}